import {type PropsWithChildren, useRef, useState} from 'react'
import {useSlots} from '@primer/react'
import {PathsProvider} from './contexts/PathsContext'
import type {Paths} from './types'
import {ButtonProvider} from './contexts/ButtonContext'
import {AnchorButton} from './components/anchor-button'
import {DialogWrapper} from './components/dialog-wrapper'
import {EnforcementProvider} from './contexts/EnforcementContext'

export type ImportBranchProtectionDialogProps = PropsWithChildren<{
  paths: Paths
  onOpen?: () => void
  onSuccess?: () => void
}>

ImportBranchProtectionDialog.AnchorButton = AnchorButton

const slotConfig = {
  anchorButton: ImportBranchProtectionDialog.AnchorButton,
}

export {ImportBranchProtectionDialog}

export const DialogState = {
  closed: 'closed',
  selection: 'selection',
  deletion: 'deletion',
} as const
type DialogState = (typeof DialogState)[keyof typeof DialogState]

function ImportBranchProtectionDialog({children, paths, onOpen, onSuccess}: ImportBranchProtectionDialogProps) {
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.closed)
  const isOpen = dialogState !== DialogState.closed
  const buttonRef = useRef<HTMLButtonElement>(null)
  const onDialogOpen = () => {
    onOpen?.()
    setDialogState(DialogState.selection)
  }
  const onDialogClose = () => setDialogState(DialogState.closed)
  const wrappedOnSuccess = () => {
    onSuccess?.()
    setDialogState(DialogState.closed)
  }
  const [{anchorButton}] = useSlots(children, slotConfig)

  return (
    <PathsProvider paths={paths}>
      <ButtonProvider onClick={onDialogOpen} buttonRef={buttonRef}>
        <EnforcementProvider>
          {anchorButton || <AnchorButton>Import branch protection rules</AnchorButton>}
          {isOpen && (
            <DialogWrapper
              dialogState={dialogState}
              setDialogState={setDialogState}
              onClose={onDialogClose}
              onSuccess={wrappedOnSuccess}
            />
          )}
        </EnforcementProvider>
      </ButtonProvider>
    </PathsProvider>
  )
}
